import { useAtomValue, useSetAtom } from 'jotai'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { type UserMe } from 'shared/types/user'
import { type LoginStatus, type PasswordRegistrationStatus, deleteAuthSession } from 'shared/api/authApi'
import {
  authSessionAtom,
  authChallengeStepAtom,
  isAuthenticatedAtom,
  mfaRequiredAtom,
  newPasswordRequiredAtom
} from 'shared/auth/state/authAtoms'
import { performLoginAttempt, completePasswordRegistration, LoginRestartRequiredError } from 'shared/auth/logic/login'
import { submitMfaCode, resendMfaCode, mapConfirmMfaError } from 'shared/auth/logic/mfa'
import {
  requestPasswordReset,
  confirmPasswordReset,
  changeCurrentPassword
} from 'shared/auth/logic/accountActions'
import { clearLegacyCognitoCookies } from 'shared/auth/legacyCognitoCookies'
import apiClient from 'shared/api/axiosBase'
import { setUserMeCache, loadUserMe } from 'shared/api/userMeQuery'
import { useGTM } from 'shared/hooks/use-gtm'

/**
 * 認証の唯一の入口。旧 AuthContext が公開していた setter は廃止し、
 * 状態は読み取り専用、遷移はすべてこのフックのアクション経由にする。
 *
 * - server state(userMe)は TanStack Query
 * - 認証クライアント state は jotai(authAtoms)
 * - リダイレクトは持たない(route guard の責務)
 * - ログイン途中の状態とセッションは api の HttpOnly Cookie が運ぶ(クライアントは識別子を持たない)
 */
export const useAuth = () => {
  const queryClient = useQueryClient()
  const { trackLogout } = useGTM()

  const setSession = useSetAtom(authSessionAtom)
  const setChallengeStep = useSetAtom(authChallengeStepAtom)

  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const mfaRequired = useAtomValue(mfaRequiredAtom)
  const newPasswordRequired = useAtomValue(newPasswordRequiredAtom)

  /** userMe をキャッシュへ入れてセッションを認証済みにする。 */
  const markAuthenticated = useCallback((userMe: UserMe) => {
    setUserMeCache(queryClient, userMe)
    setChallengeStep(null)
    setSession('authenticated')
  }, [queryClient, setChallengeStep, setSession])

  /** userMe を取得して認証済みにする。取得できなければ未認証へ倒す。 */
  const establishSession = useCallback(async (): Promise<void> => {
    try {
      const userMe = await loadUserMe(queryClient)
      markAuthenticated(userMe)
    } catch {
      setUserMeCache(queryClient, null)
      setChallengeStep(null)
      setSession('unauthenticated')
    }
  }, [queryClient, markAuthenticated, setChallengeStep, setSession])

  /** メール/パスワードでログインを試みる。api の応答に応じて状態を進める。 */
  const login = useCallback(async (email: string, password: string): Promise<LoginStatus> => {
    const status = await performLoginAttempt(email, password)

    if (status === 'passwordRegistrationRequired') {
      setChallengeStep('newPassword')
      return status
    }
    if (status === 'mfaRequired') {
      setChallengeStep('mfa')
      return status
    }

    await establishSession()
    return status
  }, [establishSession, setChallengeStep])

  /**
   * 進行中の challenge を破棄してログイン前へ戻す。
   * 途中状態が失効した場合や、新パスワード設定は済んだが後続に失敗した場合に、
   * 完了済み・失効済みの途中状態へ再送信して詰むのを避けるために使う。
   */
  const resetChallenge = useCallback(() => {
    setChallengeStep(null)
  }, [setChallengeStep])

  /** MFA(確認コード)を送信する。成功で認証済みにする。 */
  const confirmMfa = useCallback(async (code: string): Promise<void> => {
    try {
      await submitMfaCode(code)
    } catch (error) {
      const mapped = mapConfirmMfaError(error)
      if (mapped.restartLogin) {
        resetChallenge()
      }
      return await Promise.reject(mapped)
    }
    // userMe 無しで認証済みにすると tenantId などを前提にする画面が壊れるため、login と同じく
    // 取得できなければ未認証へ倒してログインからやり直させる(セッション Cookie は発行済みなので、
    // 再読み込みでも初回判定が拾う)。
    const userMe = await loadUserMe(queryClient).catch(() => null)
    if (userMe != null) {
      markAuthenticated(userMe)
      return
    }
    setUserMeCache(queryClient, null)
    setChallengeStep(null)
    setSession('unauthenticated')
    return await Promise.reject({ ...mapConfirmMfaError(null), restartLogin: true })
  }, [queryClient, markAuthenticated, resetChallenge, setChallengeStep, setSession])

  /** 確認コードを再送する(状態は変えない)。 */
  const resendCode = useCallback(async (): Promise<void> => {
    await resendMfaCode()
  }, [])

  /**
   * 仮パスワードで招待されたユーザーの初回パスワードを登録する。
   * 続けて MFA が要求されれば mfa へ、そうでなければ challenge を解消する。
   * userMe 取得・認証確定は呼び出し側(登録フロー)が markAuthenticated で行う。
   * 途中状態が失効していればログイン前へ戻す。
   */
  const completeNewPassword = useCallback(async (password: string): Promise<PasswordRegistrationStatus> => {
    let status: PasswordRegistrationStatus
    try {
      status = await completePasswordRegistration(password)
    } catch (error) {
      if (error instanceof LoginRestartRequiredError) {
        resetChallenge()
      }
      throw error
    }
    setChallengeStep(status === 'mfaRequired' ? 'mfa' : null)
    return status
  }, [resetChallenge, setChallengeStep])

  /**
   * セッションを破棄し、状態を初期化する(GTM 送信なし)。
   * ログアウトのほか、セッション切れ・パスワード変更による全セッション失効の後始末に使う。
   *
   * legacy Cookie(Cognito / saml_token)の受理期間中は `DELETE /v1/auth/session` だけでは
   * legacy Cookie が残り、次のリクエストで再び認証されてしまう。saml_token は
   * `DELETE /saml/session`、Cognito Cookie はクライアント側の削除で併せて消す。
   */
  const endSession = useCallback(async (): Promise<void> => {
    // SAML SSO の saml_token cookie 削除。saml_token が無くても 204。
    try {
      await apiClient.delete('/saml/session')
    } catch (e) {
      console.warn('SAML session 削除に失敗しました:', e)
    }
    // api_session の失効。Cookie が無い・無効でも 204。
    try {
      await deleteAuthSession()
    } catch (e) {
      console.warn('API session 削除に失敗しました:', e)
    }
    clearLegacyCognitoCookies()
    setUserMeCache(queryClient, null)
    setChallengeStep(null)
    setSession('unauthenticated')
  }, [queryClient, setChallengeStep, setSession])

  /** ログアウト。 */
  const logout = useCallback(async (): Promise<void> => {
    // GTM は失敗しても後続をブロックしない。
    try {
      trackLogout()
    } catch (e) {
      console.warn('trackLogout に失敗しました:', e)
    }
    await endSession()
  }, [trackLogout, endSession])

  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    await requestPasswordReset(email)
  }, [])

  const confirmForgotPassword = useCallback(async (email: string, code: string, newPassword: string): Promise<void> => {
    await confirmPasswordReset(email, code, newPassword)
  }, [])

  /**
   * パスワードを変更する。api が全 API セッションを失効するため、成功したらセッションを破棄して
   * ログインし直させる(ログイン画面への遷移は route guard が行う)。
   */
  const changePassword = useCallback(async (oldPassword: string, newPassword: string): Promise<void> => {
    await changeCurrentPassword(oldPassword, newPassword)
    await endSession()
  }, [endSession])

  return {
    // 読み取り(setter は公開しない)
    isAuthenticated,
    mfaRequired,
    newPasswordRequired,
    // アクション
    login,
    confirmMfa,
    resendCode,
    resetChallenge,
    completeNewPassword,
    markAuthenticated,
    endSession,
    logout,
    forgotPassword,
    confirmForgotPassword,
    changePassword
  }
}
